import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import App from './App.tsx';
import { AuthProvider } from './contexts/AuthContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { GameErrorBoundary } from './components/errors/GameErrorBoundary';
import { Capacitor } from '@capacitor/core';
import { App as CapacitorApp } from '@capacitor/app';
import { Browser } from '@capacitor/browser';
import { supabase } from './lib/supabase/client';
import { installGlobalErrorTracking } from './lib/errors/errorTracking';
import { initSentry } from './lib/sentry';
import { ensureRulesetReady } from './lib/hooks/useActiveRuleset';
import { getBrand } from './lib/branding';
// Shared with game-main.tsx so a brand cannot be right in one entry and
// wrong in the other — see the header of deepLinkBrand.ts.
import {
  OAUTH_CALLBACK_SCHEME,
  OAUTH_PROJECT_HOST,
  routeAfterDeepLinkAuth,
} from './lib/auth/deepLinkBrand';
import { captureAttributionOnVisit } from './lib/attribution/captureAttribution';
import { initReferralBootstrap } from './lib/referral/referralBootstrap';
// Side-effect import: applies persisted reduced-motion / high-contrast
// settings as <html> data-attributes at boot (SettingsModal is lazy, so
// without this the attributes would only apply after Settings opens).
import './lib/accessibility/applyAccessibilitySettings';
import './app/globals.css';

// Web service-worker cleanup. The marketing/web build no longer ships a PWA
// (scripts/build-web.cjs forces VITE_DISABLE_PWA=true), but a workbox SW from
// the v1.7.x "coming soon" era precached index.html and still runs in browsers
// / in-app WebViews (e.g. Bluesky) that visited then — serving the stale page.
// The self-destroying /sw.js (public/sw.js) is the primary fix; this is
// belt-and-suspenders for any client that loads fresh HTML while still carrying
// a registered SW: unregister it and drop its caches so the next load is clean.
// (game-main.tsx does the same for the native shell; this covers the web.)
if (
  !Capacitor.isNativePlatform() &&
  typeof navigator !== 'undefined' &&
  'serviceWorker' in navigator
) {
  navigator.serviceWorker
    .getRegistrations()
    .then((regs) => Promise.all(regs.map((r) => r.unregister())))
    .catch(() => {});
  if (typeof caches !== 'undefined') {
    caches
      .keys()
      .then((keys) => Promise.all(keys.map((k) => caches.delete(k))))
      .catch(() => {});
  }
}

// 🚀 BASIC TEST: This should appear in ALL builds

// Sentry must be initialised before the global error tracking hook so that
// the errorTrackingSink finds window.Sentry when it checks available sinks.
initSentry();
installGlobalErrorTracking('main');

// Q1.1: capture install attribution on first visit. Idempotent —
// first-touch wins, repeat calls are no-ops. Stashes UTM/ref/path
// to localStorage; persistAttribution() flushes to DB at signup.
captureAttributionOnVisit();

// Referral bootstrap — capture any /i/<code> or ?ref=<code> from the
// launch URL + attach Capacitor deep-link listener for native. The
// claim itself fires from useReferral once the user is authenticated.
initReferralBootstrap();

// P1.4: kick off the active-ruleset fetch at boot so the cache is
// populated by the time the user taps Start. Fire-and-forget — the
// gameplay path falls back to DEFAULT_POKER_RULESET (which shares
// its id with the seed DB row) if this hasn't resolved by then, so
// session stamping is FK-safe either way. Failure is silent inside
// ensureRulesetReady; the .catch is just to satisfy promise hygiene.
//
// POKER ONLY (StackedStreak-x4ma): Echidna's ruleset is the compiled
// ECHIDNA_RULESET — the game_rulesets/experiments tables exist only on
// the PokerBlitz project, so on the Echidna site this fetch was pure
// 404 noise against the wrong database.
if (getBrand().key === 'poker_blitz') {
  void ensureRulesetReady().catch(() => {
    // intentionally swallowed — gameplay never blocks on ruleset fetch
  });
}

// StackedStreak-3xr: top of the funnel. Fires once per app launch
// (browser refresh / native cold start). Pair with `game_start` and
// `game_complete` to compute open→play and play→complete conversion.
//
// StackedStreak-xggw: initialize() MUST run before any logEvent —
// the manager's internal `initialized` flag gates every emission, so
// app_open (and every subsequent event) is silently dropped without
// it. On web, initialize() is trivial (sets the flag and returns);
// on native it also wires Firebase user-properties.
import('./lib/analytics/AnalyticsManager').then(async ({ analyticsManager }) => {
  await analyticsManager.initialize();
  void analyticsManager.logEvent('app_open', {
    platform: Capacitor.isNativePlatform() ? Capacitor.getPlatform() : 'web',
    timestamp: Date.now(),
  });
}).catch(() => {
  // analytics is best-effort — never block app boot on it.
});

// 🚀 CRITICAL: Register Capacitor App listener IMMEDIATELY on script load
// This must happen before React components mount to catch deep links
if (Capacitor.isNativePlatform()) {
  
  // Wait for Capacitor App plugin to be available
  let retryCount = 0;
  const maxRetries = 50; // 5 seconds total (50 * 100ms)
  
  const waitForCapacitorApp = () => {
    retryCount++;
    
    if (CapacitorApp && CapacitorApp.addListener) {
      
      // Add the listener IMMEDIATELY
      const listener = CapacitorApp.addListener('appUrlOpen', (data) => {
        // Detect if this is likely an Apple SSO callback
        const isAppleSSO = data.url.includes(OAUTH_CALLBACK_SCHEME) || 
                          data.url.includes('apple') ||
                          data.url.includes('provider=apple');
        
        console.log('🔗 Deep Link Received:', {
          url: data.url,
          containsCallback: data.url.includes(OAUTH_CALLBACK_SCHEME),
          containsTokens: data.url.includes('access_token'),
          containsCode: data.url.includes('code='),
          containsSupabase: data.url.includes(OAUTH_PROJECT_HOST),
          isAppleSSO,
          timestamp: new Date().toISOString(),
        });

        // Process the deep link URL.
        //
        // This entry file is SHARED — both index.html and echidna.html load
        // src/main.tsx — so the scheme and project host have to follow the
        // brand rather than being hardcoded to Poker Blitz's. They previously
        // were, which was harmless only because the generic `code=` /
        // `access_token=` arms below happen to catch an Echidna callback too;
        // the routing at the end of the flow did NOT follow, and sent Echidna
        // to Poker Blitz's /auth/callback route (StackedStreak-vn4q).
        if (data.url.includes(OAUTH_CALLBACK_SCHEME) ||
            data.url.includes('access_token=') ||
            data.url.includes('code=') ||
            data.url.includes(OAUTH_PROJECT_HOST)) {

          // Dismiss the SFSafariViewController / Chrome Custom Tabs sheet
          // that AuthContext opened for the OAuth flow. Without this, the
          // in-app browser stays on screen behind the app and the user has
          // to swipe it away. Best-effort — close() rejects if no browser
          // is open (e.g. universal-link cold start), which is fine.
          void Browser.close().catch(() => {});

          // Extract tokens from the deep link URL
          let accessToken = null;
          let refreshToken = null;
          let error = null;
          let code = null; // Apple OAuth might use code= instead of access_token=
          
          // Parse the URL to extract tokens
          try {
            const url = new URL(data.url);
            const hash = url.hash.substring(1); // Remove the #
            
            // Try to extract from hash first (web OAuth flow)
            if (hash && hash.includes('access_token=')) {
              const hashParams = new URLSearchParams(hash);
              accessToken = hashParams.get('access_token');
              refreshToken = hashParams.get('refresh_token');
              error = hashParams.get('error');
              code = hashParams.get('code'); // Apple might use code flow
              
              console.log('✅ Found tokens in hash:', {
                hasAccessToken: !!accessToken,
                hasRefreshToken: !!refreshToken,
                hasCode: !!code,
                hasError: !!error,
                provider: hashParams.get('provider') || 'unknown',
              });
            }
            
            // If not in hash, try query params (custom scheme might use this)
            if (!accessToken && url.search) {
              const queryParams = new URLSearchParams(url.search.substring(1));
              accessToken = queryParams.get('access_token');
              refreshToken = queryParams.get('refresh_token');
              error = queryParams.get('error');
              code = queryParams.get('code'); // Apple might use code flow
              
              if (accessToken || code) {
                console.log('✅ Found tokens/code in query params:', {
                  hasAccessToken: !!accessToken,
                  hasRefreshToken: !!refreshToken,
                  hasCode: !!code,
                  hasError: !!error,
                  provider: queryParams.get('provider') || 'unknown',
                });
              }
            }
            
            // Also check for error in query params if not found in hash
            if (!error && url.search) {
              const queryParams = new URLSearchParams(url.search.substring(1));
              error = queryParams.get('error');
            }
            
            // Apple OAuth specific: Check if we got a code instead of tokens (authorization code flow)
            if (!accessToken && code) {
              // Apple SSO authorization-code flow — Supabase exchanges it
              // server-side. If only a code arrives without tokens, the
              // redirect may not be completing as expected.
            }
          } catch (e) {
            console.error('❌ Failed to parse deep link URL:', e);
          }
          
          if (error) {
            console.error('❌ OAuth error from deep link:', {
              error,
              isAppleSSO,
              url: data.url,
              errorType: error.includes('server_error') ? 'server_error' : 
                       error.includes('access_denied') ? 'access_denied' :
                       error.includes('user_denied') ? 'user_denied' : 'unknown',
            });
            // Convert to user-friendly error message
            const friendlyError = error.includes('server_error') 
              ? 'Unable to connect to authentication server. Please check your internet connection and try again.'
              : error.includes('access_denied') || error.includes('user_denied')
              ? 'Sign in was cancelled. Please try again if you want to continue.'
              : error;
            // Navigate with error so user sees feedback
            routeAfterDeepLinkAuth(friendlyError);
            return;
          }
          
          // PKCE flow (Supabase JS v2 default): server returns ?code=... and the client
          // must call exchangeCodeForSession with the code_verifier it stashed in
          // localStorage during signInWithOAuth. The dead code path here used to log
          // and navigate away — no exchange ever happened, getSession() kept returning
          // null, and the user landed back on the sign-in screen (App Store rejection
          // 2.1(a) on iPad Air M3 / iPadOS 26.4.2 against build 132). Do the swap
          // here, then hand off to /auth/callback for profile-completion routing.
          if (!accessToken && code) {
            console.log('🔐 PKCE code received from deep link — exchanging for session...', {
              isAppleSSO,
            });
            supabase.auth.exchangeCodeForSession(code).then(({ data: exchangeData, error: exchangeError }) => {
              if (exchangeError) {
                console.error('❌ exchangeCodeForSession failed:', exchangeError);
                const friendlyError = exchangeError.message?.includes('server_error')
                  ? 'Unable to connect to authentication server. Please check your internet connection and try again.'
                  : exchangeError.message || 'Sign in failed. Please try again.';
                routeAfterDeepLinkAuth(friendlyError);
                return;
              }
              if (!exchangeData?.session) {
                console.error('❌ exchangeCodeForSession returned no session');
                routeAfterDeepLinkAuth('Sign in session expired. Please try signing in again.');
                return;
              }
              console.log('✅ PKCE exchange successful, session established. Routing to callback.', {
                userId: exchangeData.session.user?.id,
                provider: exchangeData.session.user?.app_metadata?.provider,
              });
              routeAfterDeepLinkAuth();
            }).catch((err) => {
              console.error('❌ PKCE exchange threw:', err);
              routeAfterDeepLinkAuth('Sign in failed. Please try again.');
            });
            return;
          }
          
          if (accessToken) {
            console.log('✅ Found OAuth tokens, setting session...', {
              isAppleSSO,
              hasRefreshToken: !!refreshToken,
            });
            // Set the session manually with the tokens
            supabase.auth.setSession({
              access_token: accessToken,
              refresh_token: refreshToken || ''
            }).then(async ({ data, error }) => {
              if (error) {
                console.error('❌ Failed to set session from deep link:', error);
                // Convert to user-friendly error message
                const friendlyError = error.message?.includes('server_error')
                  ? 'Unable to connect to authentication server. Please check your internet connection and try again.'
                  : error.message || 'Sign in failed. Please try again.';
                // Navigate with error
                routeAfterDeepLinkAuth(friendlyError);
                return;
              }
              
              if (!data.session) {
                console.warn('⚠️ No session returned from setSession');
                routeAfterDeepLinkAuth('Sign in session expired. Please try signing in again.');
                return;
              }

              // Validate that user data exists. Same pattern as game-main.tsx
              // — Supabase declares session.user as required so TS narrows
              // it to `never` inside this branch; cast for the logging.
              if (!data.session.user) {
                console.error('❌ Session exists but user object is missing');
                const userMaybe = (data.session.user as unknown) as { id?: string; email?: string } | null | undefined;
                console.log('Session data:', {
                  hasSession: !!data.session,
                  hasUser: !!data.session.user,
                  userId: userMaybe?.id,
                });
                routeAfterDeepLinkAuth('Unable to retrieve user information. Please try signing in again.');
                return;
              }

              // Wait for session to propagate (give Supabase client time to update)
              console.log('✅ Session established from deep link, waiting for propagation...');
              console.log('User data received:', {
                userId: data.session.user.id,
                hasEmail: !!data.session.user.email,
                provider: data.session.user.app_metadata?.provider,
                userMetadata: data.session.user.user_metadata ? 'present' : 'missing',
              });

              // Verify session is accessible via getSession (ensures it's propagated)
              let retries = 0;
              const maxRetries = 5;
              let sessionVerified = false;

              while (retries < maxRetries && !sessionVerified) {
                await new Promise(resolve => setTimeout(resolve, 200)); // Wait 200ms
                const { data: verifyData, error: verifyError } = await supabase.auth.getSession();
                
                if (verifyError) {
                  console.warn(`⚠️ Session verification attempt ${retries + 1} failed:`, verifyError);
                  retries++;
                  continue;
                }
                
                if (verifyData.session && verifyData.session.user) {
                  console.log('✅ Session verified and propagated');
                  sessionVerified = true;
                } else {
                  console.warn(`⚠️ Session not yet propagated (attempt ${retries + 1}/${maxRetries})`);
                  retries++;
                }
              }

              if (!sessionVerified) {
                console.error('❌ Session failed to propagate after retries');
                routeAfterDeepLinkAuth('Session could not be established. Please try signing in again.');
                return;
              }

              // Navigate to callback page to handle profile checks and proper flow
              console.log('✅ Session verified, navigating to callback page');
              routeAfterDeepLinkAuth();
            });
          } else {
            console.error('❌ EARLY INIT: No access token or code found in deep link URL from appUrlOpen');
            console.error('🔍 EARLY INIT: Full URL breakdown from appUrlOpen (Apple SSO Triage):', {
              isAppleSSO,
              href: data.url,
              search: new URL(data.url).search,
              hash: new URL(data.url).hash ? '***' : '(empty)', // Don't log actual hash
              pathname: new URL(data.url).pathname,
              hashLength: new URL(data.url).hash?.length || 0,
              searchLength: new URL(data.url).search?.length || 0,
              containsCallback: data.url.includes('auth/callback'),
              containsApple: data.url.includes('apple'),
              suggestion: 'Apple OAuth may be redirecting without tokens. Check Supabase Apple provider configuration.',
            });
            
            // If this is the Apple callback URL but no tokens, navigate to callback anyway
            // The callback page might be able to handle this scenario
            if (data.url.includes(OAUTH_CALLBACK_SCHEME)) {
              // Apple SSO callback URL without tokens — let /auth/callback
              // handle whatever state we got (Supabase may have cached the session).
              routeAfterDeepLinkAuth();
            }
          }
        }
      });
      
      
      // Store the listener for potential cleanup (though it's not critical for this use case)
      (window as Window & { __capacitorAppListener?: unknown }).__capacitorAppListener = listener;
    } else {
      if (retryCount < maxRetries) {
        // Retry after a short delay
        setTimeout(waitForCapacitorApp, 100);
      } else {
        // Gave up retrying — Capacitor App plugin never registered.
        // Intentionally silent: in browser-only builds the App plugin is
        // legitimately absent and we don't want to spam Sentry.
      }
    }
  };

  // Start checking for Capacitor App availability
  waitForCapacitorApp();
} else {
  // Not a native Capacitor platform — back-button handling lives in
  // App.tsx for the web build. Intentional no-op branch.
}

// Initialize AudioManager. Dynamic import keeps the audio stack (bundled
// with the game-logic chunk) out of index.html's modulepreload critical
// path — landing-page first paint no longer blocks on 196 KB of game JS.
// initialize() still runs at startup once the chunk arrives, same as before.
// POKER BLITZ ONLY — see the matching note in game-main.tsx. Echidna has no
// sound, and its bundle no longer ships the audio files at all.
if (getBrand().key !== 'echidna') {
  void import('./lib/audio/AudioManager').then(({ audioManager }) => {
    audioManager.initialize().catch(console.error);
  });
}

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <GameErrorBoundary>
      <AuthProvider>
        <ThemeProvider>
          <App />
        </ThemeProvider>
      </AuthProvider>
    </GameErrorBoundary>
  </StrictMode>
);